/**
 * El estado de la partida en el navegador.
 *
 * La reducción es una función pura y vive aparte del enganche de React: montar un componente para
 * comprobar un `switch` esconde lo que se está comprobando.
 *
 * **El socket se crea una vez por código de sala** y sobrevive a los repintados. Recrearlo en cada
 * render abriría y cerraría conexiones sin parar, y cada apertura cuenta como una sesión nueva que
 * desplaza a la anterior: el alumno se expulsaría a sí mismo a cada tecla.
 */

import { useEffect, useRef, useState } from 'react';
import {
  anchorFrom,
  type GameErrorKey,
  type GamePhase,
  type HostLeaderboardRow,
  type HostLeaderboardView,
  type HostLobbyView,
  type HostQuestionView,
  type HostRoomInfo,
  type RevealView,
  type ServerMessage,
  type StudentLeaderboardView,
  type StudentLobbyView,
  type StudentQuestionView,
  type TimerAnchor,
  type TimerPayload,
} from '@clica/shared';
import { supabase } from '../auth/supabase.js';
import { readDeviceId } from './device.js';
import { GameSocket, type ConnectionStatus } from './socket.js';

export interface GameState {
  readonly status: ConnectionStatus;
  readonly closeCode: number | null;
  readonly phase: GamePhase;
  readonly lobby: HostLobbyView | StudentLobbyView | null;
  readonly question: HostQuestionView | StudentQuestionView | null;
  readonly reveal: RevealView | null;
  readonly leaderboard: HostLeaderboardView | StudentLeaderboardView | null;
  /** El podio final. Solo llega al proyector. */
  readonly podium: readonly HostLeaderboardRow[] | null;
  /**
   * Qué sala es esta y cómo está configurada. **Solo llega al profesor.**
   *
   * Nada de esto viajaba antes, y de ahí salían dos cosas visibles: la pizarra se titulaba «Sala
   * del profesor» —un nombre que no dice nada de lo que hay proyectado— y el enlace al informe
   * apuntaba a una ruta inventada porque no había identificador que ponerle detrás.
   */
  readonly room: HostRoomInfo | null;
  /** Cuántos puntos de control no se han guardado. Mayor que cero es un aviso, no un detalle. */
  readonly unsaved: number;
  /** El anclaje del cronómetro. Nulo significa **parado**, no «cero segundos». */
  readonly anchor: TimerAnchor | null;
  readonly frozen: 'paused' | 'hostAway' | null;
  readonly hintMd: string | null;
  readonly answered: number;
  readonly total: number;
  readonly score: number;
  /** Lo que este cliente ha pulsado, aunque el servidor todavía no lo haya confirmado. */
  readonly myOptionId: string | null;
  readonly countdownStartsAt: string | null;
  /**
   * El último error del servidor, con su número de orden.
   *
   * **Un error de partida es un suceso, no un estado.** Se pintaba como un párrafo rojo en el
   * flujo de la pantalla y nadie lo limpiaba: los tres únicos sitios que ponían `error: null`
   * —`lobby:update`, `question:open` y una respuesta aceptada— no llegan nunca después de un PIN
   * inexistente ni de un apodo rechazado. El párrafo se quedaba ahí el resto de la sesión,
   * empujando el contenido, mucho después de que el problema hubiera dejado de ser cierto.
   *
   * `seq` existe para poder reaccionar a un error **repetido**: dos apodos rechazados seguidos por
   * el mismo motivo son dos sucesos, y con solo el objeto no habría forma de distinguirlos.
   */
  readonly error: {
    readonly key: GameErrorKey;
    readonly params?: Record<string, string>;
    readonly seq: number;
  } | null;
  readonly replaced: boolean;
}

const INITIAL: GameState = {
  status: 'connecting',
  closeCode: null,
  phase: 'lobby',
  lobby: null,
  question: null,
  reveal: null,
  leaderboard: null,
  podium: null,
  room: null,
  unsaved: 0,
  anchor: null,
  frozen: null,
  hintMd: null,
  answered: 0,
  total: 0,
  score: 0,
  myOptionId: null,
  countdownStartsAt: null,
  error: null,
  replaced: false,
};

/** La dirección del socket, derivada del origen: `https` → `wss`, `http` → `ws`. */
export function socketUrl(origin: string = window.location.origin): string {
  return `${origin.replace(/^http/, 'ws')}/api/ws`;
}

/** El reloj monotónico, con una salida para las pruebas. */
export function monoNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/**
 * Reancla el cronómetro con **cualquier** mensaje que traiga el reloj.
 *
 * Atarlo solo a la apertura de la pregunta no sobrevivía a una pausa: el cliente seguía restando
 * tiempo monotónico y llegaba a cero mientras el servidor tenía la pregunta abierta.
 */
function reanchor(
  state: GameState,
  timer: TimerPayload,
  mono: number,
  rttMs: number,
): Pick<GameState, 'anchor' | 'frozen'> {
  void state;
  return { anchor: anchorFrom(timer, mono, rttMs), frozen: timer.frozen };
}

export function apply(
  state: GameState,
  message: ServerMessage,
  context: { readonly mono: number; readonly rttMs: number } = { mono: 0, rttMs: 0 },
): GameState {
  switch (message.type) {
    case 'lobby:update':
      // Una sala que llega borra el error anterior: si estamos dentro, lo que fallara ya no aplica.
      return { ...state, lobby: message.lobby, error: null };

    case 'game:countdown':
      return { ...state, phase: 'countdown', countdownStartsAt: message.startsAt, reveal: null };

    case 'question:open':
      return {
        ...state,
        phase: 'running',
        question: message.question,
        // Cada pregunta empieza limpia: sin pista, sin revelación y sin respuesta propia.
        reveal: null,
        hintMd: null,
        myOptionId: null,
        answered: 0,
        error: null,
        countdownStartsAt: null,
        ...reanchor(
          state,
          {
            endsAt: message.question.endsAt,
            serverNow: message.question.serverNow,
            frozen: message.question.frozen,
          },
          context.mono,
          context.rttMs,
        ),
      };

    case 'tick':
      return {
        ...state,
        answered: message.answered,
        total: message.total,
        ...reanchor(state, message.timer, context.mono, context.rttMs),
      };

    case 'question:hint':
      return { ...state, hintMd: message.hintMd };

    case 'answer:ack':
      // Un rechazo retira la confirmación optimista y dice por qué (RF-013).
      return message.accepted
        ? { ...state, myOptionId: message.optionId, error: null }
        : {
            ...state,
            myOptionId: null,
            ...(message.reason === undefined
              ? {}
              : { error: { key: message.reason, seq: (state.error?.seq ?? 0) + 1 } }),
          };

    case 'question:closed':
      return { ...state, anchor: null };

    case 'question:reveal':
      return {
        ...state,
        phase: 'reveal',
        reveal: message.reveal,
        anchor: null,
        score: message.reveal.view === 'student' ? message.reveal.you.totalPoints : state.score,
      };

    case 'leaderboard':
      return { ...state, leaderboard: message.leaderboard };

    case 'game:ended':
      return {
        ...state,
        phase: 'ended',
        anchor: null,
        question: null,
        ...('podium' in message ? { podium: message.podium } : {}),
        ...('unsaved' in message && message.unsaved !== undefined
          ? { unsaved: message.unsaved }
          : {}),
        ...('leaderboard' in message && message.leaderboard !== undefined
          ? { leaderboard: message.leaderboard }
          : {}),
      };

    case 'state':
      return {
        ...state,
        phase: message.phase,
        lobby: message.lobby,
        question: message.question,
        // Quien recarga la pestaña del proyector recibe `state`, no `room:info`.
        ...('room' in message ? { room: message.room } : {}),
        ...('score' in message ? { score: message.score } : {}),
        // Quien vuelve a mitad de pregunta recupera el tiempo que queda **de verdad**.
        ...(message.question === null
          ? { anchor: null, frozen: null }
          : reanchor(
              state,
              {
                endsAt: message.question.endsAt,
                serverNow: message.question.serverNow,
                frozen: message.question.frozen,
              },
              context.mono,
              context.rttMs,
            )),
        ...(message.question !== null && 'yourOptionId' in message.question
          ? { myOptionId: message.question.yourOptionId }
          : {}),
      };

    case 'room:info':
      return { ...state, room: message.room };

    case 'session:replaced':
      return { ...state, replaced: true };

    case 'error':
      return {
        ...state,
        error: {
          key: message.key,
          ...(message.params === undefined ? {} : { params: message.params }),
          seq: (state.error?.seq ?? 0) + 1,
        },
      };

    default:
      // **Un mensaje que este cliente no conoce no puede dejarlo sin estado.** Sin esta rama, el
      // `switch` devolvía `undefined` y la pantalla se quedaba en blanco: exactamente lo que le
      // pasaría a quien tuviera la pestaña abierta desde antes de un despliegue que añade eventos.
      return state;
  }
}

export interface UseGameOptions {
  readonly pin: string;
  /** Se llama al abrirse la conexión, para mandar la entrada con alias si toca. */
  readonly onOpen?: (socket: GameSocket) => void;
}

export function useGame({ pin, onOpen }: UseGameOptions): {
  readonly state: GameState;
  readonly socket: GameSocket | null;
} {
  const [state, setState] = useState<GameState>(INITIAL);
  const socketRef = useRef<GameSocket | null>(null);
  const rttRef = useRef(0);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    const socket = new GameSocket({
      url: socketUrl(),
      deviceId: readDeviceId(),
      pin,
      token: async () => {
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token ?? null;
      },
      onStatus: (status, closeCode) => {
        setState((previous) => ({ ...previous, status, closeCode }));
        if (status === 'open') onOpenRef.current?.(socket);
      },
      onMessage: (message) => {
        // El tic se contesta **en el acto**: el servidor mide el ida y vuelta con su propio reloj,
        // y cualquier retraso que metamos aquí se convierte en latencia atribuida a la red.
        if (message.type === 'tick') socket.send({ type: 'pong', tickId: message.tickId });

        setState((previous) =>
          apply(previous, message, { mono: monoNow(), rttMs: rttRef.current }),
        );
      },
    });

    socketRef.current = socket;
    socket.connect();

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [pin]);

  return { state, socket: socketRef.current };
}
